import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NavController } from '@ionic/angular';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable, of, Subject, throwError } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { DELEGATED_CONSENT_WRONG_TENANT } from '@app/services/delegated-activation/delegated-activation.constants';
import { DelegatedActivationService } from '@app/services/delegated-activation/delegated-activation.service';
import { PageTitleService } from '@app/services/page-title/page-title.service';
import { PlatformService } from '@app/services/platform/platform.service';
import { SanitizationService } from '@app/services/sanitization/sanitization.service';
import { ITenant } from '@app/services/tenant/models/tenant.model';
import { TenantService } from '@app/services/tenant/tenant.service';

/**
 * Raised when the link was minted for a tenant this session is not signed in to. Its own type
 * rather than a flag, so the one failure this flow can usefully explain is routed to its
 * explanation instead of the silent fallback every other failure takes.
 */
class WrongTenantError extends Error {}

/**
 * Landing page for the cloud-storage delegated-consent activation link.
 *
 * The user arrives from an e-mailed link or a QR code, of the shape
 * /activate-delegated?kind=onedrive|sharepoint&providerId=..&directory=..&redirect_uri=..
 * The route is guarded, so the tenant comes from the signed-in session rather than the link.
 * This page validates the four parameters, calls the backend activate-{kind}-delegated
 * endpoint and redirects to the Microsoft consent URL it returns.
 *
 * Silent by product decision: no spinner dialog, no error dialog. Every failure quietly lands
 * the user on a safe authenticated page, so the logs are the only diagnosis.
 *
 * The page never remains the destination — on web the redirect replaces it, on native it
 * navigates away after handing off to the system browser. See workflows/delegated-consent.md.
 */
@Component({
  selector: 'app-activate-delegated',
  templateUrl: './activate-delegated.page.html',
  styleUrls: ['./activate-delegated.page.scss'],
  standalone: false
})
export class ActivateDelegatedPage implements OnInit, OnDestroy {

  // Where the user is left once this route has nothing more to do: either the activation
  // could not be started, or it handed off to the system browser. Kept silent per product
  // decision — no dialog is shown.
  private readonly SAFE_ROUTE = '/print-jobs';

  // Where a wrong-tenant link is sent instead. Already the outcome page for this flow, so the
  // user is told the consent did not complete in the same place they would have been told it
  // did — and it is unguarded, so it works from either entry point.
  private readonly OUTCOME_ROUTE = '/consent-done';

  private unSubscribe = new Subject<void>();
  private logger: Logger;

  constructor(
    private activatedRoute: ActivatedRoute,
    private delegatedActivationService: DelegatedActivationService,
    private inAppBrowser: InAppBrowser,
    private loggingService: LoggingService,
    private navController: NavController,
    private pageTitleService: PageTitleService,
    private platformService: PlatformService,
    private sanitizationService: SanitizationService,
    private tenantService: TenantService
  ) {
    this.logger = this.loggingService.getLogger('[ActivateDelegatedPage]');
    const methodName = 'ctor';
    this.logger.entry(methodName);
  }

  ngOnInit(): void {
    // pageTitle is a shared signal, so skipping this leaves the previous page's title.
    this.pageTitleService.setTitle('');
    const methodName = 'ngOnInit()';
    this.logger.entry(methodName);
    this.startActivation();
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  // ############### ACTIVATION ###############

  private startActivation(): void {
    const methodName = 'startActivation()';

    const params = this.activatedRoute.snapshot.queryParamMap;
    const kind = this.sanitizationService.validateDelegatedActivationKind(params.get('kind'));
    const providerId = this.sanitizationService.validateUuid(params.get('providerId'));
    const directory = this.sanitizationService.validateDirectorySegment(params.get('directory'));
    const redirectUri = this.sanitizationService.validateDelegatedRedirectUri(params.get('redirect_uri'));

    if (!kind || !providerId || !directory || !redirectUri) {
      this.logger.error(methodName + ' Missing or invalid activation parameters');
      this.navigateToSafeRoute();
      return;
    }

    // Debug rather than info: this flow shows the user nothing, so the logs are the only
    // diagnosis (see delegated-consent.md §3), but the four values are only of interest when
    // something has actually gone wrong.
    this.logger.debug(methodName + ' kind: ' + kind + ', providerId: ' + providerId +
      ', directory: ' + directory + ', redirectUri: ' +
      this.sanitizationService.sanitizeLogOutput(redirectUri));

    this.ensureTenantLoaded().pipe(
      switchMap((tenant: ITenant) => {
        const tenantSelfLink = this.sanitizationService.validateApiUrl(tenant?.links?.self);
        if (!tenantSelfLink) {
          this.logger.error(methodName + ' Current tenant self link unavailable');
          return throwError(() => new Error('No tenant self link'));
        }
        if (!this.linkBelongsToCurrentTenant(tenant, redirectUri)) {
          return throwError(() => new WrongTenantError());
        }
        return this.delegatedActivationService.getDelegatedActivationLink(
          tenantSelfLink, kind, providerId, directory, redirectUri);
      }),
      take(1),
      // take(1) completes on the first emission but does not cancel on destroy. Without this
      // a response arriving after the user has left — Android back, browser back — would
      // still act: opening the consent browser, or navigating them out of wherever they went.
      takeUntil(this.unSubscribe)
    ).subscribe({
      next: (consentUrl: string) => {
        this.redirectToConsent(consentUrl);
      },
      error: (error) => {
        if (error instanceof WrongTenantError) {
          this.explainWrongTenant();
          return;
        }
        this.logger.error(methodName + ' Activation failed - status: ' + (error?.status ?? 'unknown'));
        this.navigateToSafeRoute();
      }
    });
  }

  // ############### WRONG TENANT ###############

  /**
   * True when the link was minted on a host the session's tenant is served on.
   *
   * The link carries no tenant id, so the activation runs against whatever tenant the session
   * resolved to. That is usually the link's own tenant: the link is minted on that tenant's host,
   * `AuthPage` feeds `location.hostname` into resolution, and step 1 auto-selects a match — and web
   * sessions are per-origin `localStorage`, so being signed in to another tenant elsewhere is
   * invisible here rather than competing.
   *
   * Three ways it still ends up elsewhere, none of them a routine mistake:
   *
   * - the follower is not the creator — a forwarded link, or a shared browser. The backend
   *   anticipates this shape too; it is what DELEGATED_CONSENT_USER_MISMATCH is for.
   * - the user lost access between creating the scan and following the link.
   * - step 1 simply misses. It compares `ITenantLite.tenantData.hostingDomain` — a *different*
   *   field, see below — with a case-sensitive `===`, so an alias host can fail to match there and
   *   match here, or the reverse.
   *
   * See delegated-consent.md §3.
   *
   * Note the two host fields are **not** the same field: this compares
   * `ITenantData.hostingDomains` from the full tenant resource, while
   * `resolvePostAuthNavigation$()` compares the singular `hostingDomain` on a tenant-*list*
   * entry (`ITenantLite`). Nothing in the app establishes that the two agree, so do not
   * "simplify" one into the other.
   *
   * `redirect_uri` is the discriminator, not `location.hostname`, for two reasons. It travels
   * *with* the link, so it still identifies the intended tenant on the `/go` *Authorize* entry
   * point, where the current URL is an in-app route rather than the link. And it works on
   * Cordova, where `location.hostname` is `localhost` — a hostname comparison there would
   * reject every in-app activation and silently disable the feature on iOS and Android.
   *
   * **Fails open.** An empty host list, or a `redirect_uri` whose host cannot be read, proceeds
   * exactly as before. Wrongly telling a user their consent belongs to another Printix home, when
   * it would have worked, is worse than the silent 403 this replaces.
   *
   * The empty case is narrow rather than routine: `deserializeTenant()` is the only producer of
   * an `ITenant` and it returns one only when the response carries `hostingDomains`, so in
   * practice this fires only if the API sends that field as something other than an array of
   * usable strings.
   *
   * **What it does not cover:** a `redirect_uri` minted on a host that is genuinely the tenant's
   * but absent from `hostingDomains`. That is a backend contract, not something this can detect —
   * the host list is all it has to compare against — and such a link is refused rather than
   * failed open, on the `/go` path too. If a legitimate consent is ever diverted, suspect that
   * before suspecting the caller.
   *
   * @param tenant - The tenant the session resolved to
   * @param redirectUri - The already-validated redirect_uri from the link
   * @returns True to continue with the activation
   */
  private linkBelongsToCurrentTenant(tenant: ITenant, redirectUri: string): boolean {
    const methodName = 'linkBelongsToCurrentTenant()';
    const linkHost = this.readHostname(redirectUri);
    // Compared against every host, not just tenantData.hostingDomain: a tenant with a regional
    // or alias host would otherwise be told its own consent link belongs elsewhere.
    const tenantHosts = tenant?.tenantData?.hostingDomains ?? [];
    if (!linkHost || tenantHosts.length === 0) {
      this.logger.info(methodName + ' hosts not comparable, continuing with the activation');
      return true;
    }

    const matches = tenantHosts.some((host: string) => host.toLowerCase() === linkHost);
    if (!matches) {
      this.logger.info(methodName + ' redirect_uri host is not served by the current tenant ' +
        JSON.stringify({ linkHost }));
    }
    return matches;
  }

  // The value has already been through validateDelegatedRedirectUri(), which parsed it, so the
  // catch is for safety rather than an expected path.
  private readHostname(url: string): string | null {
    try {
      return new URL(url).hostname.toLowerCase();
    } catch (error) {
      return null;
    }
  }

  /**
   * Sends the user to the outcome page with the wrong-tenant reason, rather than taking the
   * silent fallback.
   *
   * The exception to "this flow is silent". Silence is right where the user can do nothing — a
   * malformed link, a transient 500 — but here there is at least something to say. Whether the
   * advice fits depends on how they got here: someone following a forwarded link can switch home,
   * someone whose access was removed cannot. Either way it beats the alternative, which is a user
   * who believes they authorized a scan that then quietly times out.
   */
  private explainWrongTenant(): void {
    const methodName = 'explainWrongTenant()';
    this.logger.info(methodName + ' link is for another tenant, explaining instead of activating');
    this.navController.navigateRoot([this.OUTCOME_ROUTE], {
      queryParams: { error_code: DELEGATED_CONSENT_WRONG_TENANT }
    });
  }

  /**
   * Returns the current tenant, hydrating the last-used tenant from storage first when
   * the app was cold-started directly onto this landing route (tenantSignal not yet set).
   */
  private ensureTenantLoaded(): Observable<ITenant> {
    const tenant = this.tenantService.tenantSignal();
    if (tenant?.links?.self) {
      return of(tenant);
    }
    return this.tenantService.refreshStoredTenant().pipe(
      switchMap(() => {
        const refreshed = this.tenantService.tenantSignal();
        return refreshed ? of(refreshed) : throwError(() => new Error('No tenant available'));
      })
    );
  }

  private redirectToConsent(consentUrl: string): void {
    const methodName = 'redirectToConsent()';
    this.logger.info(methodName + ' Redirecting to Microsoft consent');
    if (this.platformService.isCordova()) {
      // Open in the system browser so the OS / Microsoft session cookies are shared.
      this.inAppBrowser.create(consentUrl, '_system');
      // Handing off to the system browser leaves this page mounted, unlike the web branch
      // where the navigation replaces it. Consent is granted and reported entirely in that
      // browser, so there is nothing left for this route to show — without this the user
      // returns to the app and finds the spinner still turning, with no way forward.
      this.navigateToSafeRoute();
    } else {
      // Deliberately not followed by navigateToSafeRoute(): the assignment already replaces
      // this page, and an Angular navigation racing it could win and cancel the redirect.
      this.assignLocation(consentUrl);
    }
  }

  /**
   * Sends the browser to an external URL.
   *
   * Its own method as a test seam: this is the branch that runs in production, and a spec
   * cannot assign window.location.href without navigating the runner away.
   *
   * @param url - The validated external URL to send the browser to
   */
  protected assignLocation(url: string): void {
    window.location.href = url;
  }

  private navigateToSafeRoute(): void {
    this.navController.navigateRoot([this.SAFE_ROUTE]);
  }
}
